import { useState, useEffect } from "react";
import { parseBackendDateTimeParts } from "../../utils/formatDate";
import { notifyError, notifySucces } from "../../utils/notifications";
import { FinishTicketParams, PendingTicketInfoModalProps } from "./types";
import { usePermissions } from "../../Context/permissionContext";
import api from "../../services/api";
import { isCustomError } from "../../utils/validations";
import { storageService } from "../../services/storageService";

const DEFAULT_CLASSIFICATION = "all_no_group";

interface ErrorMessage {
  message: string;
}

export function usePendingTicketInfoModal({
  onClose,
  onContinue,
  clientId,
  onCreateNew,
  ticketDate,
  ticketsList: initialTicketsList,
}: Pick<PendingTicketInfoModalProps, "onClose" | "onContinue" | "clientId" | "onCreateNew" | "ticketDate" | "ticketsList">) {
  const { permissions } = usePermissions();

  const [internalTicketsList, setInternalTicketsList] = useState(initialTicketsList || []);
  const [isClassificationModalOpen, setIsClassificationModalOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<string | number | null>(null);
  const [shouldCreateNewAfterFinish, setShouldCreateNewAfterFinish] = useState(true);

  const hasPermissionToFinishWithoutClassification = permissions.includes(
    "finish_without_class"
  );

  useEffect(() => {
    if (initialTicketsList) setInternalTicketsList(initialTicketsList);
  }, [initialTicketsList]);

  const isBusy = !!loadingAction;
  const parts = parseBackendDateTimeParts(ticketDate);
  const day = parts?.day;
  const month = parts?.month;
  const year = parts?.year;
  const hours = parts?.hours;
  const minutes = parts?.minutes;
  const formattedTicketDate = parts ? `${day}/${month}/${year}` : "";
  const ticketTime = parts ? `${hours}:${minutes}` : "";

  const handleContinue = async (ticketId: string | number) => {
    try {
      setLoadingAction(`continue_${ticketId}`);

      const response = await api.patch("handle_ticket_status", {
        ticket_id: ticketId,
        clientId
      });

      const hasCustomError = isCustomError(response);
      if (hasCustomError) {
        notifyError(response.message, "", false, true);
        return;
      }

      storageService.setTicketProtocol(ticketId);

      onClose();
      onContinue(response);

    } catch (err) {
      console.error(err);
      const error = err as ErrorMessage;
      notifyError(error?.message || "Erro ao assumir protocolo.");
    } finally {
      setLoadingAction(null);
    }
  };


  const handleCreateGlobal = async () => {
    try {
      setLoadingAction('global_create');
      const response = await api.post("start_new_ticket", {
        clientId,
      });
      const ticketFromBack = response.data.data?.ticket || response.data.ticket;

      storageService.setTicketProtocol(ticketFromBack.id);
      onClose();

      await onCreateNew({
        ticket: ticketFromBack,
      });

    } catch (err) {
      console.error(err);
      notifyError("Não foi possível criar um novo protocolo no momento.");
    } finally {
      setLoadingAction(null);
    }
  };


  const handleInternalFinish = async (
    params: FinishTicketParams,
    ticketId: string | number,
    isCreateNew: boolean
  ) => {
    try {
      const endpoint = isCreateNew ? "finish_and_create_ticket" : "tickets/finish";

      setLoadingAction(isCreateNew ? `finish_create_${ticketId}` : `finish_only_${ticketId}`);

      const payload = {
        ...params,
        clientId,
        ticketId,
        classificationId: params.classificationId === DEFAULT_CLASSIFICATION
          ? undefined : params.classificationId
      };

      const response = await api.post(`${endpoint}`, payload);

      const hasCustomError = isCustomError(response);
      if (hasCustomError) {
        notifyError(response.message, "", false, true);
        return;
      }

      setIsClassificationModalOpen(false);


      if (isCreateNew) {
        storageService.setTicketProtocol(response?.data?.ticket.id);
        onClose();
        onCreateNew({ ticket: response.data.ticket });
      } else {
        notifySucces("Atendimento finalizado com sucesso!");
        storageService.removeTicketProtocol();
        const updatedList = response?.data?.ticketsList;
        if (updatedList) {
          setInternalTicketsList(updatedList);
        }

      }
    } catch (err) {
      console.error(err);
      const error = err as ErrorMessage;
      notifyError(error?.message || "Erro ao finalizar protocolo.");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleOpenFinishOnly = async (ticketId: string | number) => {
    setSelectedTicketId(ticketId);
    setShouldCreateNewAfterFinish(false);

    try {
      if (!hasPermissionToFinishWithoutClassification) {
        setIsClassificationModalOpen(true);
        return;
      }
      setLoadingAction(`finish_only_${ticketId}`);

      await handleInternalFinish(
        {
          classificationName: "Sem Classificar",
          subClassificationName: "Sem SubClassificar",
        },
        ticketId,
        false
      );
    } catch (err) {
      console.error(err);
      const error = err as ErrorMessage;
      notifyError(error?.message || "Erro ao finalizar protocolo.");
    } finally {
      setLoadingAction(null);
    }
  };


  const handleFinishAndCreateNew = async (ticketId: string | number) => {
    setSelectedTicketId(ticketId);
    setShouldCreateNewAfterFinish(true);

    try {
      if (!hasPermissionToFinishWithoutClassification) {
        setIsClassificationModalOpen(true);
        return;
      }

      await handleInternalFinish(
        {
          classificationName: "Sem Classificar",
          subClassificationName: "Sem SubClassificar",
        },
        ticketId,
        true
      );
    } catch (err) {
      console.error(err);
      const error = err as ErrorMessage;
      notifyError(error?.message);
    }
  };

  return {
    internalTicketsList,
    isClassificationModalOpen,
    setIsClassificationModalOpen,
    selectedTicketId,
    shouldCreateNewAfterFinish,
    loadingAction,
    isBusy,
    formattedTicketDate,
    ticketTime,
    handleCreateGlobal,
    handleContinue,
    handleFinishAndCreateNew,
    handleInternalFinish,
    handleOpenFinishOnly
  };
}